import { css } from "@emotion/react";
import type { HTMLAttributes, PropsWithChildren } from "react";
import { useControlled } from "@exonn/hooks";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";

export const ToolbarButton = ({
  children,
  isActive: isActiveProp,
  defaultIsActive = false,
  onActivate,
  onClick,
  showLeftSeparator = false,
  showRightSeparator = false,
  count = 0,
  ...rest
}: PropsWithChildren<
  {
    isActive?: boolean;
    defaultIsActive?: boolean;
    onActivate?: (active: boolean) => void;
    showLeftSeparator?: boolean;
    showRightSeparator?: boolean;
    count?: number;
  } & HTMLAttributes<HTMLButtonElement>
>) => {
  const [isActive, setIsActive] = useControlled({
    name: "ToolbarButton",
    state: "isActive",
    controlled: isActiveProp,
    default: defaultIsActive,
  });
  return (
    <button
      onClick={(e) => {
        setIsActive(!isActive);
        onActivate?.(!isActive);
        onClick?.(e);
      }}
      css={styles.root({
        isActive,
      })}
      {...rest}
    >
      {children}
      {!!count && (
        <TextV2 variant={"body2"} css={styles.count}>
          {count}
        </TextV2>
      )}
    </button>
  );
};

const styles = {
  count: css`
    position: absolute;
    top: -7px;
    right: -8px;
    min-width: 15px;
    min-height: 15px;
  `,
  root:
    ({ isActive }: { isActive: boolean }) =>
    (theme: ExactTheme) => css`
      position: relative;
      border: none;
      padding: 0;
      margin: 0;
      width: 25px;
      height: 25px;
      display: flex;
      justify-content: center;
      border-radius: ${theme.borderRadiusV2.secondary};
      align-items: center;
      color: ${isActive ? theme.colorsV2.blue[500] : theme.colorsV2.fg};
      background-color: transparent;
      cursor: pointer;
    `,
};
